import Util from '../../../util/util';
import DeleteFileMutation from '../../../../../mutations/delete_file_mutation.modern';
import {collectReferencedFileIds} from './references';

/** The task's content as the editors last serialized it — never Relay store state. */
export type FinalContent = {description: string | null | undefined; comments: (string | null | undefined)[]};

export type PendingImageDeletes = {
	note: (fileId: string) => void;
	size: () => number;
	resolve: (taskId: string, finalContent: FinalContent) => void;
};

/**
 * Holds inline images removed from the text during one task-modal session and
 * deletes the orphans when the modal closes.
 *
 * Deletion is deferred rather than fired on save because undo lives only as long
 * as the editor: removing an image, clicking away, then undoing must not cost
 * the user their picture. It is not deferred beyond modal close because the
 * editor unmounts there and the undo history goes with it, so a longer window
 * could not enable an undo — it would only serve manual recovery, which was
 * weighed and declined.
 *
 * The refcount runs against the task's final state across the description and
 * every comment, so an image that was re-added, duplicated, or moved between
 * surfaces survives.
 *
 * If resolve never runs — a crash, a hard navigation — the file simply stays,
 * unreferenced, and appears in the Files list as an ordinary attachment. Nothing
 * dangles and nothing is lost.
 */
export const createPendingImageDeletes = (): PendingImageDeletes => {
	const removed = new Set<string>();

	return {
		note: fileId => {
			removed.add(fileId);
		},

		size: () => removed.size,

		/**
		 * `finalContent` must come from the editors, not from the Relay store.
		 *
		 * Closing the modal blurs the active editor to trigger its save and then
		 * navigates in the same tick, so this runs while that mutation is still in
		 * flight and the store still holds the previous value. Refcounting against
		 * the store would miss a just-re-added image and delete a file the
		 * persisted description still references.
		 */
		resolve: (taskId, finalContent) => {
			if (removed.size === 0) return;

			const stillReferenced = new Set([
				...collectReferencedFileIds(finalContent.description),
				...finalContent.comments.flatMap(comment => [...collectReferencedFileIds(comment)]),
			]);
			const orphans = [...removed].filter(id => !stillReferenced.has(id));
			removed.clear();

			if (orphans.length === 0) return;
			Util.CommitMutation(DeleteFileMutation, {files: orphans, taskId});
		},
	};
};
